import {
  collection, doc, setDoc, deleteDoc, getDoc, getDocs,
  query, where, orderBy, limit, updateDoc, serverTimestamp, onSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';

// ── LIKES ────────────────────────────────────────────────────────────────────
// One document per (liker, designer) pair, with a DETERMINISTIC id built from
// both uids. That id is what makes double-liking impossible: a second like
// writes the same document rather than adding another, so the count can't be
// inflated by clicking twice or by two tabs racing.
//
// The visible like COUNT is not maintained here — a Cloud Function trigger owns
// it (see functions/onLikeWrite.js), because the counter lives on the liked
// user's document and clients may only write their own.
const likeId = (likerUid, designerUid) => `${likerUid}__${designerUid}`;

export async function likeDesigner(likerUid, designerUid) {
  if (!likerUid || !designerUid || likerUid === designerUid) return;
  await setDoc(doc(db, 'likes', likeId(likerUid, designerUid)), {
    likerUid,
    designerUid,
    createdAt: serverTimestamp(),
  });
}

export async function unlikeDesigner(likerUid, designerUid) {
  if (!likerUid || !designerUid) return;
  await deleteDoc(doc(db, 'likes', likeId(likerUid, designerUid)));
}

/** Has this user liked this designer? A direct get by id — no query, no index. */
export async function isLiked(likerUid, designerUid) {
  if (!likerUid || !designerUid) return false;
  const snap = await getDoc(doc(db, 'likes', likeId(likerUid, designerUid)));
  return snap.exists();
}

/** The designer uids a user has liked, newest first. */
export async function fetchLikedUids(likerUid, max = 200) {
  if (!likerUid) return [];
  const snap = await getDocs(query(
    collection(db, 'likes'),
    where('likerUid', '==', likerUid),
    orderBy('createdAt', 'desc'),
    limit(max),
  ));
  return snap.docs.map(d => d.data().designerUid).filter(Boolean);
}

// ── NOTIFICATIONS ────────────────────────────────────────────────────────────
// Created only by the trigger above; clients may read and mark their own.
export async function fetchNotifications(uid, max = 30) {
  if (!uid) return [];
  const snap = await getDocs(query(
    collection(db, 'notifications'),
    where('toUid', '==', uid),
    orderBy('createdAt', 'desc'),
    limit(max),
  ));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * The same query, LIVE.
 *
 * A like used to appear only after a reload or after opening the panel, which
 * is a strange thing for a notification to do — the point of the badge is that
 * it tells you without being asked. This is one listener per signed-in
 * designer, on a query already bounded to 30 rows, and it replaces a fetch on
 * every mount plus another on every open.
 *
 * Returns its unsubscribe: the caller MUST call it, or the listener outlives
 * the component and keeps billing.
 */
export function subscribeNotifications(uid, cb, max = 30) {
  if (!uid) return () => {};
  return onSnapshot(
    query(
      collection(db, 'notifications'),
      where('toUid', '==', uid),
      orderBy('createdAt', 'desc'),
      limit(max),
    ),
    (snap) => cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    (err) => { console.warn('[notifications] subscribe failed:', err); cb([]); },
  );
}

export async function markNotificationsRead(ids) {
  await Promise.all(
    ids.map(id => updateDoc(doc(db, 'notifications', id), { read: true }).catch(() => {})),
  );
}

// ── WEIGHTED SAMPLING (feed / recommendations) ───────────────────────────────
/**
 * Shuffles `rows` randomly, but biased so that more-liked entries surface more
 * often. Not a straight "sort by likes": a pure ranking would show the same
 * handful of designers to everyone forever, and a new designer with no likes
 * would never be seen by anyone — the feed would freeze.
 *
 * Uses the exponential-jump form of weighted sampling without replacement: draw
 * u ∈ (0,1) per row and key it as u^(1/weight). Higher weight ⇒ key closer to 1
 * ⇒ earlier in the order, while every row keeps a real chance. Sorting by that
 * key yields a correctly weighted random permutation in one pass.
 *
 * `+1` on the weight is what keeps a zero-like row in the running at all.
 */
export function weightedShuffle(rows, weightOf = (r) => r.likeCount || 0) {
  return rows
    .map(r => {
      const w = Math.max(0, Number(weightOf(r)) || 0) + 1;
      const u = Math.random() || Number.MIN_VALUE;     // never exactly 0
      return { r, key: Math.pow(u, 1 / w) };
    })
    .sort((a, b) => b.key - a.key)
    .map(x => x.r);
}
